import React, { forwardRef, useId } from 'react';
import { InputProps, SelectProps, ButtonProps, FormFieldProps } from '../../types/ui';
import styles from '../../styles/theme.module.css';

// Input Component
export const FloralInput = forwardRef<HTMLInputElement, InputProps>((
  {
    label,
    error,
    required = false,
    disabled = false,
    className = '',
    type = 'text',
    onChange,
    'aria-describedby': ariaDescribedBy,
    'data-testid': testId,
    ...props
  },
  ref
) => {
  const id = useId();
  const errorId = `${id}-error`;
  const describedBy = error ? `${errorId} ${ariaDescribedBy || ''}`.trim() : ariaDescribedBy;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (onChange) {
      onChange(event.target.value);
    }
  };

  return (
    <div className={`${styles.formField} ${className}`}>
      {label && (
        <label htmlFor={id} className={styles.label}>
          {label}
          {required && <span className="text-red-500 ml-1" aria-label="required">*</span>}
        </label>
      )}
      <input
        ref={ref}
        id={id}
        type={type}
        disabled={disabled}
        required={required}
        aria-describedby={describedBy}
        aria-invalid={error ? 'true' : 'false'}
        data-testid={testId}
        className={`${styles.input} ${error ? styles.error : ''}`}
        onChange={handleChange}
        {...props}
      />
      {error && (
        <div id={errorId} className={styles.errorMessage} role="alert">
          {error}
        </div>
      )}
    </div>
  );
});

FloralInput.displayName = 'FloralInput';

// Select Component
export const FloralSelect = forwardRef<HTMLSelectElement, SelectProps>((
  {
    label,
    error,
    required = false,
    disabled = false,
    className = '',
    options,
    placeholder,
    'aria-describedby': ariaDescribedBy,
    'data-testid': testId,
    ...props
  },
  ref
) => {
  const id = useId();
  const errorId = `${id}-error`;
  const describedBy = error ? `${errorId} ${ariaDescribedBy || ''}`.trim() : ariaDescribedBy;

  return (
    <div className={`${styles.formField} ${className}`}>
      {label && (
        <label htmlFor={id} className={styles.label}>
          {label}
          {required && <span className="text-red-500 ml-1" aria-label="required">*</span>}
        </label>
      )}
      <select
        ref={ref}
        id={id}
        disabled={disabled}
        required={required}
        aria-describedby={describedBy}
        aria-invalid={error ? 'true' : 'false'}
        data-testid={testId}
        className={`${styles.input} ${error ? styles.error : ''}`}
        {...props}
      >
        {placeholder && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}
        {options.map((option) => (
          <option
            key={option.value}
            value={option.value}
            disabled={option.disabled}
          >
            {option.label}
          </option>
        ))}
      </select>
      {error && (
        <div id={errorId} className={styles.errorMessage} role="alert">
          {error}
        </div>
      )}
    </div>
  );
});

FloralSelect.displayName = 'FloralSelect';

// Button Component
export const FloralButton = forwardRef<HTMLButtonElement, ButtonProps>((
  {
    children,
    variant = 'primary',
    size = 'md',
    disabled = false,
    loading = false,
    type = 'button',
    className = '',
    'aria-label': ariaLabel,
    'data-testid': testId,
    onClick,
    ...props
  },
  ref
) => {
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (!disabled && !loading && onClick) {
      onClick(event);
    }
  };

  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-label={ariaLabel}
      aria-busy={loading}
      data-testid={testId}
      className={`${styles.button} ${styles[variant]} ${styles[size]} ${className}`}
      onClick={handleClick}
      {...props}
    >
      {loading ? (
        <div className={styles.spinner} aria-hidden="true" />
      ) : (
        children
      )}
    </button>
  );
});

FloralButton.displayName = 'FloralButton';

// Textarea Component
interface TextareaProps extends Omit<InputProps, 'type'> {
  rows?: number;
  cols?: number;
  resize?: 'none' | 'both' | 'horizontal' | 'vertical';
}

export const FloralTextarea = forwardRef<HTMLTextAreaElement, TextareaProps>((
  {
    label,
    error,
    required = false,
    disabled = false,
    className = '',
    rows = 4,
    resize = 'vertical',
    'aria-describedby': ariaDescribedBy,
    'data-testid': testId,
    ...props
  },
  ref
) => {
  const id = useId();
  const errorId = `${id}-error`;
  const describedBy = error ? `${errorId} ${ariaDescribedBy || ''}`.trim() : ariaDescribedBy;

  return (
    <div className={`${styles.formField} ${className}`}>
      {label && (
        <label htmlFor={id} className={styles.label}>
          {label}
          {required && <span className="text-red-500 ml-1" aria-label="required">*</span>}
        </label>
      )}
      <textarea
        ref={ref}
        id={id}
        rows={rows}
        disabled={disabled}
        required={required}
        aria-describedby={describedBy}
        aria-invalid={error ? 'true' : 'false'}
        data-testid={testId}
        className={`${styles.input} ${error ? styles.error : ''}`}
        style={{ resize }}
        {...props}
      />
      {error && (
        <div id={errorId} className={styles.errorMessage} role="alert">
          {error}
        </div>
      )}
    </div>
  );
});

FloralTextarea.displayName = 'FloralTextarea';

// Checkbox Component
interface CheckboxProps extends Omit<FormFieldProps, 'placeholder'> {
  checked?: boolean;
  defaultChecked?: boolean;
  onChange?: (checked: boolean) => void;
  value?: string;
}

export const FloralCheckbox = forwardRef<HTMLInputElement, CheckboxProps>((
  {
    label,
    error,
    required = false,
    disabled = false,
    className = '',
    checked,
    defaultChecked,
    onChange,
    'aria-describedby': ariaDescribedBy,
    'data-testid': testId,
    ...props
  },
  ref
) => {
  const id = useId();
  const errorId = `${id}-error`;
  const describedBy = error ? `${errorId} ${ariaDescribedBy || ''}`.trim() : ariaDescribedBy;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (onChange) {
      onChange(event.target.checked);
    }
  };

  return (
    <div className={`${styles.formField} ${className}`}>
      <div className="flex items-start space-x-3">
        <input
          ref={ref}
          id={id}
          type="checkbox"
          checked={checked}
          defaultChecked={defaultChecked}
          disabled={disabled}
          required={required}
          aria-describedby={describedBy}
          aria-invalid={error ? 'true' : 'false'}
          data-testid={testId}
          className="mt-1 h-4 w-4 text-sage-600 border-sage-300 rounded focus:ring-sage-500"
          onChange={handleChange}
          {...props}
        />
        {label && (
          <label htmlFor={id} className={`${styles.label} flex-1 cursor-pointer`}>
            {label}
            {required && <span className="text-red-500 ml-1" aria-label="required">*</span>}
          </label>
        )}
      </div>
      {error && (
        <div id={errorId} className={styles.errorMessage} role="alert">
          {error}
        </div>
      )}
    </div>
  );
});

FloralCheckbox.displayName = 'FloralCheckbox';

// Radio Group Component
interface RadioOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface RadioGroupProps extends Omit<FormFieldProps, 'placeholder'> {
  options: RadioOption[];
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  name: string;
}

export const FloralRadioGroup: React.FC<RadioGroupProps> = ({
  label,
  error,
  required = false,
  disabled = false,
  className = '',
  options,
  value,
  defaultValue,
  onChange,
  name,
  'aria-describedby': ariaDescribedBy,
  'data-testid': testId,
}) => {
  const groupId = useId();
  const errorId = `${groupId}-error`;
  const describedBy = error ? `${errorId} ${ariaDescribedBy || ''}`.trim() : ariaDescribedBy;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (onChange) {
      onChange(event.target.value);
    }
  };

  return (
    <fieldset className={`${styles.formField} ${className}`} data-testid={testId}>
      {label && (
        <legend className={styles.label}>
          {label}
          {required && <span className="text-red-500 ml-1" aria-label="required">*</span>}
        </legend>
      )}
      <div className="space-y-2" role="radiogroup" aria-describedby={describedBy}>
        {options.map((option, index) => {
          const optionId = `${groupId}-${index}`;
          return (
            <div key={option.value} className="flex items-center space-x-3">
              <input
                id={optionId}
                type="radio"
                name={name}
                value={option.value}
                checked={value === option.value}
                defaultChecked={defaultValue === option.value}
                disabled={disabled || option.disabled}
                required={required}
                className="h-4 w-4 text-sage-600 border-sage-300 focus:ring-sage-500"
                onChange={handleChange}
              />
              <label htmlFor={optionId} className={`${styles.label} cursor-pointer`}>
                {option.label}
              </label>
            </div>
          );
        })}
      </div>
      {error && (
        <div id={errorId} className={styles.errorMessage} role="alert">
          {error}
        </div>
      )}
    </fieldset>
  );
};